import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { BlockSkeleton, TextSkeleton } from "../loading";
import {
  emptyHeaderMessage,
  fetchHeaderMessageConfig,
  saveHeaderMessageConfig,
} from "./headerMessageApi";

const pad = (value) => String(value).padStart(2, "0");

const toDatetimeLocalValue = (date) => {
  if (!date) {
    return "";
  }

  const parsed = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(
    parsed.getDate(),
  )}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const toApiDatetimeValue = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const buildDefaultMessage = () => {
  const startsAt = new Date();
  const expiresAt = new Date(startsAt.getTime() + 24 * 60 * 60 * 1000);

  return {
    ...emptyHeaderMessage,
    startsAt: toDatetimeLocalValue(startsAt),
    expiresAt: toDatetimeLocalValue(expiresAt),
  };
};

const normalizeMessage = (message) => {
  const defaultMessage = buildDefaultMessage();

  return {
    ...defaultMessage,
    ...message,
    text: message?.text || "",
    enabled: Boolean(message?.enabled),
    startsAt: toDatetimeLocalValue(message?.startsAt) || defaultMessage.startsAt,
    expiresAt: toDatetimeLocalValue(message?.expiresAt) || defaultMessage.expiresAt,
  };
};

function HeaderMessageMaintenancePage({ utente }) {
  const { getAccessTokenSilently, user } = useAuth0();
  const [message, setMessage] = useState(buildDefaultMessage);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const canReadDebug = Boolean(utente?.permessiAPI?.["read:debug"]);
  const canWriteDebug = Boolean(utente?.permessiAPI?.["write:debug"]);

  useEffect(() => {
    let ignore = false;

    const loadConfig = async () => {
      if (!canReadDebug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const accessToken = await getAccessTokenSilently();
        const data = await fetchHeaderMessageConfig(accessToken);

        if (!ignore) {
          setMessage(normalizeMessage(data?.message || data || null));
        }
      } catch (loadError) {
        if (!ignore) {
          setMessage(buildDefaultMessage());
          setError(
            `Configurazione non caricata: ${loadError.message}. Puoi comunque preparare un nuovo messaggio.`,
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      ignore = true;
    };
  }, [canReadDebug, getAccessTokenSilently, user?.sub]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    setMessage((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setStatus("");
  };

  const handleResetDates = () => {
    setMessage((current) => ({
      ...current,
      ...buildDefaultMessage(),
      text: current.text,
      enabled: current.enabled,
    }));
    setStatus("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canWriteDebug) {
      setError("Il permesso write:debug è necessario per salvare il messaggio.");
      return;
    }

    if (!message.text.trim()) {
      setError("Inserisci un testo prima di salvare.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const accessToken = await getAccessTokenSilently();

      const payload = {
        text: message.text.trim(),
        enabled: message.enabled,
        startsAt: toApiDatetimeValue(message.startsAt),
        expiresAt: toApiDatetimeValue(message.expiresAt),
        updatedBy: utente?.username,
      };

      await saveHeaderMessageConfig(accessToken, payload);
      setStatus("Messaggio header salvato.");
    } catch (saveError) {
      setError(`Salvataggio non riuscito: ${saveError.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!canReadDebug) {
    return (
      <div className="pa2 pa3-ns">
        <div className="sfondoBiancoTema br3 pa3">
          <h3 className="mt0 mb2">Messaggio header</h3>
          <p className="mb0 dark-red">
            Non hai i permessi per visualizzare questa pagina.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pa2 pa3-ns">
      <div className="mw8 center sfondoBiancoTema br3 pa3 pa4-l">
        <h1 className="mt0 mb2">Messaggio barra header</h1>
        <p className="mt0 mb4 dark-gray">
          Configura un testo breve da mostrare accanto al menu principale. Se lo
          spazio disponibile non basta, il testo scorre senza aumentare
          l&apos;altezza della barra.
        </p>

        {loading ? (
          <div>
            <TextSkeleton lines={3} className="mb3" />
            <BlockSkeleton height={180} tone="soft" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {!canWriteDebug && (
              <div className="ba br2 pa3 mb3 dark-red b--dark-red">
                Modalità sola lettura: serve il permesso write:debug per salvare.
              </div>
            )}
            {error && (
              <div className="ba br2 pa3 mb3 dark-red b--dark-red">{error}</div>
            )}
            {status && (
              <div className="ba br2 pa3 mb3 dark-green b--dark-green">
                {status}
              </div>
            )}

            <label className="db mb3">
              <span className="db b mb2">Testo</span>
              <textarea
                name="text"
                value={message.text}
                onChange={handleChange}
                className="w-100 pa2 ba b--black-20 br2"
                rows="4"
                maxLength="500"
                placeholder="Es. Manutenzione programmata dalle 18:00 alle 19:00"
              />
              <small className="db tr dark-gray">
                {message.text.length}/500 caratteri
              </small>
            </label>

            <label className="db mb3 pointer">
              <input
                type="checkbox"
                name="enabled"
                checked={message.enabled}
                onChange={handleChange}
                className="mr2"
              />
              Messaggio attivo
            </label>

            <div className="flex flex-wrap nl2 nr2">
              <label className="db w-100 w-50-ns pa2">
                <span className="db b mb2">Data partenza</span>
                <input
                  type="datetime-local"
                  name="startsAt"
                  value={message.startsAt}
                  onChange={handleChange}
                  className="w-100 pa2 ba b--black-20 br2"
                />
              </label>
              <label className="db w-100 w-50-ns pa2">
                <span className="db b mb2">Data scadenza</span>
                <input
                  type="datetime-local"
                  name="expiresAt"
                  value={message.expiresAt}
                  onChange={handleChange}
                  className="w-100 pa2 ba b--black-20 br2"
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center mt3">
              <button
                type="submit"
                className="tastoVerde br2 bn pointer ph3 pv2 mr3 mb2"
                disabled={saving || !canWriteDebug}
              >
                {saving ? "Salvataggio..." : "Salva messaggio"}
              </button>
              <button
                type="button"
                className="tastoBianco br2 ba bordoColAlt pointer ph3 pv2 mb2"
                onClick={handleResetDates}
              >
                Reimposta date now + 24h
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default HeaderMessageMaintenancePage;
